import { useCallback } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'

import { graphqlRequest } from '../../../../services/tanstack-query'
import { queryKeys } from '../../../../services/tanstack-query/queryClient'
import { getUsersLeaveSummaries } from '../../../../graphql/custom-queries'
import { IGetUsersLeaveSummaries, IUserLeaveSummary } from '../../../../types/users'
import { createRequestBatcher } from './requestBatcher'

const PENDING_POLL_INTERVAL_MS = 4000
const PENDING_POLL_MAX_UPDATES = 5

const enqueueSummaryRequest = createRequestBatcher<string, IUserLeaveSummary>({
  batchLimit: 50,
  keyOf: (userId) => userId,
  resultKeyOf: (summary) => summary.userId,
  fallback: (userId) => ({ userId, status: 'ERROR' }),
  fetchBatch: async (requestedIds) => {
    const userIds = [...new Set(requestedIds)]
    const response = await graphqlRequest<IGetUsersLeaveSummaries>(getUsersLeaveSummaries, { userIds })
    return response.getUsersLeaveSummaries.summaries
  },
})

export interface IUseLeaveSummariesResult {
  summariesByUserId: Record<string, IUserLeaveSummary>
  refetchUser: (userId: string) => Promise<void>
}

const combineSummaries = (results: { data?: IUserLeaveSummary }[]) => ({
  summariesByUserId: Object.fromEntries(results.flatMap((result) => (result.data ? [[result.data.userId, result.data] as const] : []))),
})

export function useLeaveSummaries(userIds: string[], enabled: boolean): IUseLeaveSummariesResult {
  const queryClient = useQueryClient()

  const combined = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: queryKeys.users.leaveSummary(userId),
      queryFn: () => enqueueSummaryRequest(userId),
      enabled,
      staleTime: Infinity,
      refetchInterval: (query) => {
        const isPending = (query.state.data as IUserLeaveSummary | undefined)?.status === 'PENDING'
        return isPending && query.state.dataUpdateCount <= PENDING_POLL_MAX_UPDATES ? PENDING_POLL_INTERVAL_MS : false
      },
    })),
    combine: combineSummaries,
  })

  const refetchUser = useCallback(
    async (userId: string) => {
      const summary = await enqueueSummaryRequest(userId)
      queryClient.setQueryData(queryKeys.users.leaveSummary(userId), summary)
    },
    [queryClient]
  )

  return {
    summariesByUserId: combined.summariesByUserId,
    refetchUser,
  }
}
